import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CreateReportSubscriptionRequest,
  DeleteReportSubscriptionRequest,
  ListReportSubscriptionsRequest,
  ReportSubscriptionCompanyDetails,
  ReportSubscriptionDetails,
  ReportTemplateDetails,
  RunReportSubscriptionRequest,
  SituAnalyticsWebApiClient,
  UpdateReportSubscriptionRequest
} from 'situanalytics-webapi-client';
import { AuthService, CompanyViewService, ConfirmDialogService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';

/**
 * Manages scheduled report subscriptions. On a company's edit page (companyId set) it shows that
 * company's subscriptions; routed on its own (Admin → Report Subscriptions) it shows every company's,
 * with filters and a company picker on the form.
 * Backed by the SituAnalytics report-subscription API (Admin/Developer only).
 */
@Component({
  selector: 'app-company-report-subscriptions',
  templateUrl: './company-report-subscriptions.component.html',
  styleUrls: ['./company-report-subscriptions.component.scss']
})
export class CompanyReportSubscriptionsComponent implements OnInit {
  @Input() companyId: string = '';
  @Input() companyName: string = '';
  /** All companies, for the "also include" multi-select. Shape: { id, name }. */
  @Input() companies: { id?: string; name?: string }[] = [];

  loading: boolean = false;
  saving: boolean = false;
  subscriptions: ReportSubscriptionDetails[] = [];
  templates: ReportTemplateDetails[] = [];

  reportTypes: { value: number; label: string }[] = [
    { value: 0, label: 'Supplier Representation' },
    { value: 1, label: 'Supplier Monthly Product (PDF)' }
  ];

  /** Covers every builder, so it takes no target companies. */
  readonly supplierMonthlyProduct = 1;

  form: FormGroup;
  showForm: boolean = false;
  editingId: string | null = null;

  displayedColumns: string[] = ['enabled', 'toRecipients', 'templateName', 'includes', 'lastSentUtc', 'actions'];

  // All-subscriptions page filters.
  filterType: number | null = null;
  filterText: string = '';

  constructor(
    private analyticsClient: SituAnalyticsWebApiClient,
    private authService: AuthService,
    private companyViewService: CompanyViewService,
    private confirmDialog: ConfirmDialogService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      companyId: [null],
      reportType: [0, Validators.required],
      enabled: [true],
      toRecipients: [null, Validators.required],
      ccRecipients: [null],
      templateName: [null, Validators.required],
      reportUrl: [null],
      schedule: [null],
      reportTitle: [null],
      additionalCompanyIds: [[]]
    });
  }

  get canManage(): boolean {
    return this.authService.hasRole(Role.SituSystemsAdmin) || this.authService.hasRole(Role.SituSystemsDeveloper);
  }

  /** True when routed as the all-companies admin page rather than embedded on a company. */
  get allMode(): boolean {
    return !this.companyId;
  }

  /** Builders selectable to report on — every company except the host manufacturer itself. */
  get availableCompanies(): { id?: string; name?: string }[] {
    const hostId = ((this.allMode ? this.form.value.companyId : this.companyId) || '').toLowerCase();
    return this.companies.filter(c => c.id && c.id.toLowerCase() !== hostId);
  }

  /** Subscriptions after the all-companies page filters (none apply on a company's page). */
  get filteredSubscriptions(): ReportSubscriptionDetails[] {
    if (!this.allMode) return this.subscriptions;
    const text = this.filterText.trim().toLowerCase();
    return this.subscriptions.filter(s =>
      (this.filterType === null || s.reportType === this.filterType)
      && (!text
        || (s.company ?? '').toLowerCase().includes(text)
        || (s.reportTitle ?? '').toLowerCase().includes(text)
        || (s.toRecipients ?? '').toLowerCase().includes(text)));
  }

  ngOnInit(): void {
    if (!this.canManage) return;

    if (this.allMode) {
      this.form.controls.companyId.setValidators(Validators.required);
      this.displayedColumns = ['company', 'reportType', ...this.displayedColumns];
      this.companyViewService.LoadCompanies()
        .then(companies => this.companies = [...companies]
          .filter(c => c.id && c.name)
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')))
        .catch(() => this.notify('Failed to load companies.'));
    }

    this.loadTemplates();
    this.load();
  }

  loadTemplates(): void {
    this.analyticsClient.listReportTemplates()
      .then(res => {
        this.templates = res.success ? (res.templates ?? []) : [];
      })
      .catch(() => this.templates = []);
  }

  /** Templates that belong to the selected report type. */
  get templatesForType(): ReportTemplateDetails[] {
    const type = this.form.value.reportType ?? 0;
    return this.templates.filter(t => (t.reportType ?? 0) === type);
  }

  /** Switching type picks that type's first template, since the old one won't fit. */
  onReportTypeChange(): void {
    this.form.patchValue({ templateName: this.templatesForType[0]?.slug ?? null });
  }

  templateName(slug?: string): string {
    if (!slug) return '—';
    return this.templates.find(t => t.slug === slug)?.displayName ?? slug;
  }

  load(): void {
    this.loading = true;
    const request = new ListReportSubscriptionsRequest();
    request.companyId = this.allMode ? undefined : this.companyId;
    this.analyticsClient.listReportSubscriptions(request)
      .then(res => {
        this.subscriptions = res.success ? (res.subscriptions ?? []) : [];
        if (!res.success) this.notify(`Could not load subscriptions: ${res.errorMessages}`);
      })
      .catch(() => this.notify('Failed to load report subscriptions.'))
      .finally(() => this.loading = false);
  }

  startAdd(): void {
    this.editingId = null;
    this.showForm = true;
    this.form.reset({
      companyId: null, reportType: 0, enabled: true, toRecipients: null,
      ccRecipients: null, templateName: this.templates.find(t => (t.reportType ?? 0) === 0)?.slug ?? null,
      reportUrl: null, schedule: null, reportTitle: null, additionalCompanyIds: []
    });
  }

  startEdit(sub: ReportSubscriptionDetails): void {
    this.editingId = sub.id ?? null;
    this.showForm = true;
    this.form.reset({
      companyId: sub.companyId ?? null,
      reportType: sub.reportType ?? 0,
      enabled: sub.enabled ?? true,
      toRecipients: sub.toRecipients ?? null,
      ccRecipients: sub.ccRecipients ?? null,
      templateName: sub.templateName ?? null,
      reportUrl: sub.reportUrl ?? null,
      schedule: sub.schedule ?? null,
      reportTitle: sub.reportTitle ?? null,
      additionalCompanyIds: (sub.additionalCompanies ?? []).map(c => c.companyId).filter(Boolean)
    });
  }

  cancelEdit(): void {
    this.showForm = false;
    this.editingId = null;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.value;
    if (v.reportType !== this.supplierMonthlyProduct
      && (!v.additionalCompanyIds || v.additionalCompanyIds.length === 0)) {
      this.notify('Select at least one target company.');
      return;
    }
    this.saving = true;

    const hostId: string = this.allMode ? v.companyId : this.companyId;
    const hostName = this.allMode
      ? this.companies.find(c => c.id === hostId)?.name ?? this.subscriptions.find(s => s.id === this.editingId)?.company
      : this.companyName;

    if (this.editingId) {
      const request = new UpdateReportSubscriptionRequest();
      request.id = this.editingId;
      request.companyId = hostId;
      request.company = hostName;
      request.reportType = v.reportType;
      request.enabled = v.enabled;
      request.toRecipients = v.toRecipients;
      request.ccRecipients = v.ccRecipients;
      request.templateName = v.templateName;
      request.reportUrl = v.reportUrl;
      request.schedule = v.schedule;
      request.reportTitle = v.reportTitle;
      request.additionalCompanies = this.buildAdditionalCompanies(v.additionalCompanyIds);
      this.analyticsClient.updateReportSubscription(request)
        .then(res => this.afterSave(res.success, res.errorMessages, 'updated'))
        .catch(() => this.notify('Failed to save subscription.'))
        .finally(() => this.saving = false);
    } else {
      const request = new CreateReportSubscriptionRequest();
      request.companyId = hostId;
      request.company = hostName;
      request.reportType = v.reportType;
      request.enabled = v.enabled;
      request.toRecipients = v.toRecipients;
      request.ccRecipients = v.ccRecipients;
      request.templateName = v.templateName;
      request.reportUrl = v.reportUrl;
      request.schedule = v.schedule;
      request.reportTitle = v.reportTitle;
      request.additionalCompanies = this.buildAdditionalCompanies(v.additionalCompanyIds);
      this.analyticsClient.createReportSubscription(request)
        .then(res => this.afterSave(res.success, res.errorMessages, 'created'))
        .catch(() => this.notify('Failed to save subscription.'))
        .finally(() => this.saving = false);
    }
  }

  toggleEnabled(sub: ReportSubscriptionDetails): void {
    const request = new UpdateReportSubscriptionRequest();
    request.id = sub.id;
    request.companyId = sub.companyId;
    request.company = sub.company;
    request.reportType = sub.reportType;
    request.enabled = !sub.enabled;
    request.toRecipients = sub.toRecipients;
    request.ccRecipients = sub.ccRecipients;
    request.templateName = sub.templateName;
    request.reportUrl = sub.reportUrl;
    request.schedule = sub.schedule;
    request.reportTitle = sub.reportTitle;
    // Preserve the existing additional-company set (update replaces it).
    request.additionalCompanies = (sub.additionalCompanies ?? []).map(c =>
      new ReportSubscriptionCompanyDetails({ companyId: c.companyId, company: c.company }));
    this.analyticsClient.updateReportSubscription(request)
      .then(res => {
        if (res.success) {
          this.notify(`Subscription ${request.enabled ? 'enabled' : 'disabled'}.`);
          this.load();
        } else {
          this.notify(`Could not update: ${res.errorMessages}`);
        }
      })
      .catch(() => this.notify('Failed to update subscription.'));
  }

  remove(sub: ReportSubscriptionDetails): void {
    this.confirmDialog.open({
      title: 'Delete subscription',
      message: `Delete the report subscription for ${sub.company ?? 'this company'}? This stops the scheduled report.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      actionIcon: 'delete',
      actionColour: 'warn'
    });
    this.confirmDialog.confirmed().subscribe(confirmed => {
      if (!confirmed) return;
      const request = new DeleteReportSubscriptionRequest();
      request.id = sub.id;
      this.analyticsClient.deleteReportSubscription(request)
        .then(res => {
          if (res.success) {
            this.notify('Subscription deleted.');
            this.load();
          } else {
            this.notify(`Could not delete: ${res.errorMessages}`);
          }
        })
        .catch(() => this.notify('Failed to delete subscription.'));
    });
  }

  sendNow(sub: ReportSubscriptionDetails): void {
    this.confirmDialog.open({
      title: 'Send report now',
      message: `Generate and email the report for ${sub.company ?? 'this company'} now?`
        + ` It will be sent to: ${sub.toRecipients ?? '(no recipients set)'}.`,
      confirmText: 'Send',
      cancelText: 'Cancel',
      actionIcon: 'send',
      actionColour: 'primary'
    });
    this.confirmDialog.confirmed().subscribe(confirmed => {
      if (!confirmed) return;
      const request = new RunReportSubscriptionRequest();
      request.id = sub.id;
      this.notify('Sending report…');
      this.analyticsClient.runReportSubscription(request)
        .then(res => {
          if (res.success && res.sent) {
            this.notify('Report sent.');
            this.load();
          } else {
            this.notify(`Could not send: ${res.errorMessages}`);
          }
        })
        .catch(() => this.notify('Failed to send report.'));
    });
  }

  reportTypeLabel(value?: number): string {
    return this.reportTypes.find(t => t.value === value)?.label ?? `Type ${value}`;
  }

  /** Comma-separated names of additional companies for the table, or "—". */
  additionalCompanyNames(sub: ReportSubscriptionDetails): string {
    if (sub.reportType === this.supplierMonthlyProduct) return 'All builders';
    const names = (sub.additionalCompanies ?? []).map(c => c.company).filter(Boolean);
    return names.length ? names.join(', ') : '—';
  }

  private buildAdditionalCompanies(ids: string[]): ReportSubscriptionCompanyDetails[] {
    return (ids ?? []).map(id => new ReportSubscriptionCompanyDetails({
      companyId: id,
      company: this.companies.find(c => c.id === id)?.name
    }));
  }

  private afterSave(success: boolean | undefined, errors: string[] | undefined, verb: string): void {
    if (success) {
      this.notify(`Subscription ${verb}.`);
      this.showForm = false;
      this.editingId = null;
      this.load();
    } else {
      this.notify(`Could not save: ${errors}`);
    }
  }

  private notify(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}
